import React, { useState, useRef, useEffect } from "react";
import { Box, Button as ThemeUIButton } from "theme-ui";

import { Button, ProductButtonWithImage } from "components";
import { ArrowLeftIcon, ArrowRightIcon } from "components/icons";
import { getHrefWithHostname } from "utils";
import { useTranslation } from "hooks";
import * as styles from "./ProductSelectorCarousel.styles";

export default function ProductSelectorCarousel({
  products,
  selectProduct,
  selectedProduct,
  disableScroll = false,
  productSelectorStyles,
  itemWidth = 300,
}) {
  const { t } = useTranslation();

  const [activeSlide, setActiveSlide] = useState(0);
  const wrapperRef = useRef(null);

  const handleScroll = () => {
    const { scrollLeft } = wrapperRef.current;
    setActiveSlide(Math.round(scrollLeft / itemWidth));
  };

  const scrollCarouselOnClick = (index) => {
    wrapperRef.current.scrollLeft = itemWidth * index;
  };

  const showScrollButtons = (ref) => {
    if (!ref.current) return false;

    const visibleContainerWidth = ref.current.getBoundingClientRect().width;

    return ref.current.scrollWidth > visibleContainerWidth;
  };

  const scrollSnapAlign = (ref) => {
    if (!ref.current) return;

    // If container is wide enough to contain 2 cards, snap to start
    return ref.current.getBoundingClientRect().width > itemWidth * 2
      ? "start"
      : "center";
  };

  useEffect(() => {
    scrollCarouselOnClick(0);
  }, [JSON.stringify(products)]);

  return (
    <Box sx={styles.carousel}>
      <Box
        ref={wrapperRef}
        sx={styles.carouselScrollContainer({ disableScroll })}
        onScroll={handleScroll}
      >
        {products
          .filter(({ id }) => !selectedProduct || id === selectedProduct.id)
          .filter((product) => !product.hideProduct)
          .map(
            ({
              id,
              isDisabled,
              productButtonLinkOverride,
              category: {
                productButtonLogo,
                logo,
                productButtonProps,
                additionalLinks,
              },
            }) => (
              <Box
                sx={styles.carouselItem({
                  scrollSnapAlign: scrollSnapAlign(wrapperRef),
                })}
                key={id}
              >
                <ProductButtonWithImage
                  logo={productButtonLogo || logo}
                  isSelected={id === selectedProduct?.id}
                  isDisabled={isDisabled}
                  productButtonLinkOverride={productButtonLinkOverride}
                  onClick={() => {
                    selectProduct(id);
                  }}
                  {...productButtonProps}
                />
                {additionalLinks && (
                  <Box sx={productSelectorStyles.additionalLinks}>
                    {additionalLinks.map(({ text, href }) => {
                      return (
                        <Button
                          key={text}
                          theme="light"
                          href={getHrefWithHostname(href)}
                          sx={productSelectorStyles.additionalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          iconAfter={<ArrowRightIcon />}
                        >
                          {text}
                        </Button>
                      );
                    })}
                  </Box>
                )}

                {id === selectedProduct?.id && (
                  <ThemeUIButton
                    variant="unstyled"
                    onClick={() => selectProduct("")}
                    sx={productSelectorStyles.changeStickButton}
                  >
                    <ArrowLeftIcon />
                    {t("Change Stick")}
                  </ThemeUIButton>
                )}
              </Box>
            )
          )}
      </Box>
      {showScrollButtons(wrapperRef) && (
        <>
          <ThemeUIButton
            sx={styles.leftCarouselArrow}
            onClick={() => scrollCarouselOnClick(activeSlide - 1)}
          >
            <ArrowLeftIcon sx={styles.leftCarouselArrowIcon} />
          </ThemeUIButton>
          <ThemeUIButton
            sx={styles.rightCarouselArrow}
            onClick={() => scrollCarouselOnClick(activeSlide + 1)}
          >
            <ArrowRightIcon sx={styles.rightCarouselArrowIcon} />
          </ThemeUIButton>
        </>
      )}
    </Box>
  );
}
